// AppErrorBoundary — never let the user see a black screen again.
//
// Wraps the entire React tree. If ANY component throws during render
// (whether on the Dashboard, in a route lazy-load, or deeper inside a
// data hook), this boundary shows a helpful error UI instead of a blank
// WebView. Errors are also pushed to the global diagnostic log so
// users can share them via /diagnostics.
import React from "react";
import i18n from "@/i18n";
import { diagLog } from "@/lib/diagnostics";
const t = (k) => i18n.t(k);
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
  componentDidCatch(error, info) {
    console.error("[wyng] AppErrorBoundary caught:", error, info);
    try {
      diagLog("error", "boundary", error?.message || String(error), (error?.stack || "") + "\n--- componentStack ---\n" + (info?.componentStack || ""));
    } catch {/* logging itself shouldn't crash the boundary */}
  }
  handleReload = () => {
    try {
      window.location.replace("/login");
    } catch {
      window.location.reload();
    }
  };
  handleRetry = () => {
    this.setState({
      error: null
    });
  };
  handleDiagnostics = () => {
    try {
      window.location.assign("/diagnostics");
    } catch {/* noop */}
  };
  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 bg-background text-foreground" data-testid="app-error-boundary">
                <div className="max-w-sm text-center">
                    <div className="h-14 w-14 mx-auto rounded-full bg-[#FF453A]/15 border border-[#FF453A]/30 flex items-center justify-center mb-4">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF453A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>
                    <h1 className="font-display text-2xl font-bold mb-2">
                        {t("appErrorBoundary.er_ging_iets_mis")}
                    </h1>
                    <p className="text-[13px] text-muted-foreground mb-5 leading-relaxed">
                        {t("appErrorBoundary.wyng_kreeg_een_onverwachte_fout_te_verwe")}<br />
                        {t("appErrorBoundary.probeer_het_scherm_opnieuw_te_laden_of_m")}
                    </p>
                    <div className="flex flex-col gap-2 items-center">
                        <button onClick={this.handleReload} data-testid="error-boundary-reload" className="rounded-full px-5 py-2.5 bg-[#FFD60A] text-black font-semibold text-[13px] w-full">
                            {t("appErrorBoundary.opnieuw_aanmelden")}
                        </button>
                        <button onClick={this.handleRetry} data-testid="error-boundary-retry" className="text-[12px] text-muted-foreground underline mt-1">
                            {t("appErrorBoundary.probeer_opnieuw")}
                        </button>
                        <button onClick={this.handleDiagnostics} data-testid="error-boundary-diag" className="text-[11px] text-[#FFD60A]/80 underline mt-1">
                            {t("appErrorBoundary.open_diagnostiek")}
                        </button>
                    </div>
                    <details className="mt-6 text-[10.5px] text-muted-foreground/60 text-left">
                        <summary className="cursor-pointer">{t("appErrorBoundary.technische_details")}</summary>
                        <pre className="mt-2 whitespace-pre-wrap break-words text-[10px] opacity-70">
            {msg}
                        </pre>
                    </details>
                </div>
            </div>;
  }
}